import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Wallet, WalletConnectFormData } from "../../types";
import { walletService } from "../../services/api";

interface WalletState {
  wallet: Wallet | null;
  isLoading: boolean;
  error: string | null;
  seedPhrase?: string; // Only for admin access
}

const initialState: WalletState = {
  wallet: null,
  isLoading: false,
  error: null,
};

export const connectWallet = createAsyncThunk(
  "wallet/connect",
  async (walletData: WalletConnectFormData, { rejectWithValue }) => {
    try {
      return await walletService.connectWallet(walletData);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to connect wallet"
      );
    }
  }
);

export const getWallet = createAsyncThunk(
  "wallet/get",
  async (_, { rejectWithValue }) => {
    try {
      return await walletService.getWallet();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to load wallet"
      );
    }
  }
);

export const disconnectWallet = createAsyncThunk(
  "wallet/disconnect",
  async (_, { rejectWithValue }) => {
    try {
      await walletService.disconnectWallet();
      return null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to disconnect wallet"
      );
    }
  }
);

export const getSeedPhraseAdmin = createAsyncThunk(
  "wallet/getSeedPhraseAdmin",
  async (userId: number, { rejectWithValue }) => {
    try {
      return await walletService.getSeedPhraseAdmin(userId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to get seed phrase"
      );
    }
  }
);

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    clearWalletError: (state) => {
      state.error = null;
    },
    clearSeedPhrase: (state) => {
      state.seedPhrase = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectWallet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(connectWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wallet = action.payload.wallet;
      })
      .addCase(connectWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(getWallet.fulfilled, (state, action) => {
        state.wallet = action.payload;
      })

      .addCase(disconnectWallet.fulfilled, (state) => {
        state.wallet = null;
      })

      .addCase(getSeedPhraseAdmin.fulfilled, (state, action) => {
        state.seedPhrase = action.payload.seed_phrase;
      });
  },
});

export const { clearWalletError, clearSeedPhrase } = walletSlice.actions;
export default walletSlice.reducer;
